import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "/#servicos", label: "Serviços" },
        { href: "/#manifesto", label: "Manifesto" },
        { href: "/#trabalhos", label: "Trabalhos" },
        { href: "/#processo", label: "Processo" },
        { href: "/#contato", label: "Contato" },
    ];

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
                scrolled
                    ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 h-[76px]">
                <Link
                    to="/"
                    className="flex items-center gap-2.5 group"
                    data-testid="brand-logo"
                >
                    <img
                        src="/mascot.png"
                        alt="GreenDuck mascote"
                        className="w-9 h-9 md:w-10 md:h-10 object-contain drop-shadow-[0_0_18px_rgba(0,255,102,0.35)] group-hover:drop-shadow-[0_0_24px_rgba(0,255,102,0.55)] transition-all group-hover:-rotate-6"
                    />
                    <span
                        className="text-white text-xl md:text-[22px] font-extrabold tracking-tight"
                        style={{
                            fontFamily:
                                '"Clash Display", "Unbounded", "Space Grotesk", sans-serif',
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Green<span className="text-[#00FF66]">Duck</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="text-sm text-white/70 hover:text-white transition-colors relative group"
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#00FF66] group-hover:w-full transition-all duration-500" />
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href="/#contato"
                        className="btn-neon text-sm"
                        data-testid="nav-cta-contact"
                    >
                        Fale conosco
                        <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
                    </a>
                </div>

                <button
                    className="md:hidden text-white p-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {links.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="text-white/80 text-lg"
                                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="/#contato"
                                onClick={() => setOpen(false)}
                                className="btn-neon text-sm mt-4 w-fit"
                                data-testid="mobile-cta-contact"
                            >
                                Fale conosco
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
